import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { isFuture } from 'date-fns';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  So5FixtureEvent,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { Skeleton } from '@sorare/core/src/atoms/animations/Skeleton';
import { Drawer } from '@sorare/core/src/atoms/layout/Drawer';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM } from '@sorare/core/src/atoms/typography';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useConversionCredits } from '@sorare/core/src/hooks/useConversionCredits';
import { useInfiniteScroll } from '@sorare/core/src/hooks/useInfiniteScroll';
import {
  ClickAddToCart_Origin,
  ClickInstantBuy_Flow,
} from '@sorare/core/src/protos/events/so5/shared/events';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';
import {
  PlayerMarketDrawer,
  PlayerMarketDrawerVariant,
} from '@sorare/marketplace/src/components/player/PlayerMarketDrawer';
import { SearchTable as SearchTableBase } from '@sorare/marketplace/src/components/searchPlayerTable/SearchTable';

import { PlayerScoreDetailsView } from '#src/components/MatchView/PlayerScoreDetailsView/index.tsx';
import { isFixtureOpened } from '#src/lib/so5.ts';

import { FixtureTopPlayersTableHeader } from './FixtureTopPlayersTableHeader/index.tsx';
import { FixtureTopPlayersTableRow } from './FixtureTopPlayersTableRow/index.tsx';
import type { FixtureTopPlayersTable_so5Fixture } from './__generated__/index.graphql.ts';
import type { PlayerScoreView } from './types.ts';
import {
  AnySportPlayerScoreStats,
  BaseballPlayerScoreStats,
  FootballPlayerScoreStats,
  NBAPlayerScoreStats,
  PlayerScoreViewAttributes,
} from './types.ts';

const SearchTable = styled(SearchTableBase)`
  --first-cell-width: 200px;
  --padding-first-cell-left: var(--unit);
  --padding-first-cell-right: var(--unit);
  --padding-last-cell-left: var(--unit);
  --padding-last-cell-right: var(--unit);
  --player-stats-icon-width: 40px;
  grid-template-columns:
    var(--first-cell-width)
    repeat(calc(var(--stat-columns) - 1), minmax(110px, 1fr)) var(--player-matchups-width);

  &.upcomingFootball {
    grid-template-columns:
      var(--first-cell-width)
      repeat(calc(var(--stat-columns) - 1), minmax(110px, 1fr));
  }

  @media ${laptopAndAbove} {
    --first-cell-width: 350px;
    --padding-first-cell-left: var(--triple-unit);
    --padding-first-cell-right: var(--intermediate-unit);
    --padding-last-cell-left: var(--intermediate-unit);
    --padding-last-cell-right: var(--unit);
    --player-stats-icon-width: 80px;
  }
`;
const LoaderWrapper = styled.div`
  &.FOOTBALL {
    grid-column: 1 / 6;
  }
  &.BASEBALL {
    grid-column: 1 / 14;
  }

  & > * {
    width: 100%;
    margin: 0;
  }
`;
const EmptyState = styled(Vertical).attrs({ center: true })`
  padding: var(--triple-unit);
  width: 100%;
  background-color: var(--c-nd-50);
  border-radius: var(--unit);
`;

const DEFAULT_STATS_BY_SPORT = {
  [Sport.FOOTBALL]: {
    upcoming: [AnySportPlayerScoreStats.SCORE],
    played: [
      AnySportPlayerScoreStats.SCORE,
      FootballPlayerScoreStats.DS,
      FootballPlayerScoreStats.AA,
    ],
  },
  [Sport.NBA]: {
    upcoming: [
      NBAPlayerScoreStats.L10,
      AnySportPlayerScoreStats.PROJECTED_SCORE,
      AnySportPlayerScoreStats.GAIN_ON_PROJECTION,
      NBAPlayerScoreStats.GAMES,
      AnySportPlayerScoreStats.FORM,
      AnySportPlayerScoreStats.PRICE,
    ],
    played: [
      AnySportPlayerScoreStats.SCORE,
      NBAPlayerScoreStats.L10,
      NBAPlayerScoreStats.DIFF,
      NBAPlayerScoreStats.MIN,
      NBAPlayerScoreStats.THREE_PT,
      NBAPlayerScoreStats.REB,
      NBAPlayerScoreStats.AST,
      NBAPlayerScoreStats.STL,
      NBAPlayerScoreStats.BLK,
      NBAPlayerScoreStats.TO,
      NBAPlayerScoreStats.PTS,
    ],
  },
  [Sport.BASEBALL]: {
    upcoming: [
      AnySportPlayerScoreStats.SCORE,
      BaseballPlayerScoreStats.MATCHUPS,
      AnySportPlayerScoreStats.FORM,
      BaseballPlayerScoreStats.L5,
      BaseballPlayerScoreStats.L15,
      AnySportPlayerScoreStats.PRICE,
    ],
    played: [
      AnySportPlayerScoreStats.SCORE,
      BaseballPlayerScoreStats.AB,
      BaseballPlayerScoreStats.R,
      BaseballPlayerScoreStats.H,
      BaseballPlayerScoreStats.RBI,
      BaseballPlayerScoreStats.HR,
      BaseballPlayerScoreStats.BB,
      BaseballPlayerScoreStats.K,
      BaseballPlayerScoreStats.MATCHUPS,
    ],
  },
};

const getDefaultStatsManager = (
  sport: Sport,
  isUpcoming: boolean
): PlayerScoreView => ({
  [PlayerScoreViewAttributes.PLAYER_SCORE]:
    DEFAULT_STATS_BY_SPORT[sport][isUpcoming ? 'upcoming' : 'played'],
  [PlayerScoreViewAttributes.OPPONENT]: [],
});

type Props = {
  eventType?: So5FixtureEvent;
  so5Fixture?: FixtureTopPlayersTable_so5Fixture | null;
  loadMore: (cursor?: string | null) => void;
  loading: boolean;
};
export const FixtureTopPlayersTable = ({
  eventType,
  so5Fixture,
  loadMore,
  loading,
}: Props) => {
  const { sport } = useSportContext();
  const [marketPlayerSlug, setMarketPlayerSlug] = useState<string | null>(null);

  const { so5FixturePlayers } = so5Fixture || {};
  const [playerScoreParams, setPlayerScoreParams] = useState<{
    gameId: string;
    playerSlug: string;
  }>();
  const { conversionCredits } = useConversionCredits({
    sport,
  });
  const canUseConversionCredit = conversionCredits?.some(cc => !cc.disabled);

  const isUpcomingFixture = !!so5Fixture && isFixtureOpened(so5Fixture);
  const statsManager = getDefaultStatsManager(sport, isUpcomingFixture);

  const { InfiniteScrollLoader } = useInfiniteScroll(
    () => {
      loadMore(so5FixturePlayers?.pageInfo?.endCursor);
    },
    !!so5FixturePlayers?.pageInfo?.hasNextPage,
    loading
  );

  if (!loading && so5FixturePlayers?.nodes.length === 0) {
    return (
      <EmptyState>
        <BodyM>
          <FormattedMessage
            id="fixture.topPlayers.emptyState"
            defaultMessage="No players found"
          />
        </BodyM>
      </EmptyState>
    );
  }

  const dislpayTotalScore =
    sport === Sport.BASEBALL &&
    !isUpcomingFixture &&
    eventType?.toUpperCase() === So5FixtureEvent.CLASSIC;

  return (
    <>
      {!!isUpcomingFixture &&
        !!isFuture(so5Fixture?.projectedLineupsReadyAt || '') && (
          <EmptyState>
            <BodyM>
              <FormattedMessage
                id="fixture.topPlayers.powerGrades"
                defaultMessage="Power Grades will appear when starting lineups are in."
              />
            </BodyM>
          </EmptyState>
        )}

      {!!playerScoreParams && (
        <Drawer open={!!playerScoreParams} side="right">
          <PlayerScoreDetailsView
            side="right"
            playerSlug={playerScoreParams?.playerSlug}
            gameId={playerScoreParams?.gameId}
            onClose={() => setPlayerScoreParams(undefined)}
          />
        </Drawer>
      )}
      {sport !== Sport.FOOTBALL && (
        <PlayerMarketDrawer
          onClose={() => setMarketPlayerSlug(null)}
          playerSlug={marketPlayerSlug}
          sport={sport}
          canUseConversionCredit={canUseConversionCredit}
          variant={PlayerMarketDrawerVariant.SCOUTING_CENTER}
          origin={{
            instantBuy: ClickInstantBuy_Flow.TOP_PLAYERS,
            addToCart: ClickAddToCart_Origin.TOP_PLAYERS,
          }}
        />
      )}
      <SearchTable
        className={classNames({
          upcomingFootball: isUpcomingFixture && sport === Sport.FOOTBALL,
        })}
        nbStatsColumns={
          statsManager[PlayerScoreViewAttributes.PLAYER_SCORE].length +
          (dislpayTotalScore ? 1 : 0)
        }
        header={
          <FixtureTopPlayersTableHeader
            statsManager={statsManager}
            displayTotalScore={dislpayTotalScore}
            rightAlignLastCell={sport === Sport.BASEBALL}
          />
        }
      >
        {(so5FixturePlayers?.nodes || []).map((so5FixturePlayer, index) => {
          const { player, eligibleSo5Scores } = so5FixturePlayer || {};
          const { anyGame: defaultGame } = eligibleSo5Scores.at(0) || {};

          return (
            <FixtureTopPlayersTableRow
              key={so5FixturePlayer.id}
              index={index}
              so5FixturePlayer={so5FixturePlayer}
              statsManager={statsManager}
              isUpcomingFixture={isUpcomingFixture}
              onClick={() =>
                setPlayerScoreParams({
                  gameId: defaultGame?.id || '',
                  playerSlug: player.slug,
                })
              }
              onListingsClick={() =>
                setMarketPlayerSlug(so5FixturePlayer.player.slug)
              }
              eventType={eventType}
            />
          );
        })}
        <LoaderWrapper className={sport}>
          <InfiniteScrollLoader>
            <Skeleton
              style={{
                width: '100%',
                height: '60px',
                display: 'flex',
              }}
            />
          </InfiniteScrollLoader>
        </LoaderWrapper>
      </SearchTable>
    </>
  );
};

FixtureTopPlayersTable.fragments = {
  so5Fixture: gql`
    fragment FixtureTopPlayersTable_so5Fixture on So5Fixture {
      slug
      projectedLineupsReadyAt
      so5FixturePlayers(
        first: 10
        positions: $positions
        myCardsRarities: $myCardsRarities
        competitionSlugs: $competitionSlugs
        after: $after
      ) {
        nodes {
          id
          eligibleSo5Scores {
            id
            anyGame {
              id
            }
          }
          ...FixtureTopPlayersTableRow_fixturePlayer
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
      ...isFixtureOpened_so5Fixture
    }
    ${FixtureTopPlayersTableRow.fragments.fixturePlayer}
    ${isFixtureOpened.fragments.so5Fixture}
  ` as TypedDocumentNode<FixtureTopPlayersTable_so5Fixture>,
};
